import { getScopedTranslate } from '@locale/translate';

import type { FieldMappingFieldItem, FieldMappingGroup } from './field-mapping';

const locale = {
  en: {
    traveller: 'Traveller',
    company: 'Company',
    journeyDetails: 'Journey details',
    journeyData: 'Journey data',
    action: 'Action',
  },
};

export const labelSeparator = ' • ';

export const isTravellerVariableSource = (variableSource: FieldMappingFieldItem['source'] = '') => {
  return (
    variableSource === 'company_traveller' ||
    variableSource === 'traveller' ||
    variableSource === 'traveller_journey' ||
    variableSource.startsWith('company_traveller_attribute') ||
    variableSource.startsWith('journey_attribute')
  );
};

// A variable whose source/action was deleted or severed carries a `_MISSING`
// suffix (mirrors the backend MISSING_SOURCE_ACTION_POSTFIX).
export const MISSING_SOURCE_SUFFIX = '_MISSING';

// The single frontend definition of "broken" -- editor pill, header chips and
// preview helpers all call this; don't add a second. It relies on the persisted
// `_MISSING` marker, whereas the backend decides by graph reachability, so the
// two can disagree (the marker can be missing on a truly broken var). Durable
// fix: a server-stamped `isBroken` so every surface agrees.
export const isVariableSourceMissing = ({
  source = '',
  action = '',
}: {
  source?: string;
  action?: string;
}): boolean =>
  (source === 'action' && action.endsWith(MISSING_SOURCE_SUFFIX)) ||
  (source.startsWith('journey_attribute') && source.endsWith(MISSING_SOURCE_SUFFIX)) ||
  (source.startsWith('company_traveller_attribute') && source.endsWith(MISSING_SOURCE_SUFFIX));

const getVariableSource = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
): string => {
  const { t } = getScopedTranslate(locale);
  if (isTravellerVariableSource(variable.source)) {
    return t('traveller');
  }
  if (variable.source === 'company') {
    return t('company');
  }
  if (variable.source === 'journey') {
    return t('journeyDetails');
  }
  if (variable.source === 'journey_data') {
    return t('journeyData');
  }
  if (variable.source === 'action') {
    const action = variableGroups?.find((group) => group.id === variable.action);
    return action?.label ?? t('action');
  }
  return variable.source;
};

type VariableFieldRef = Partial<Pick<FieldMappingFieldItem, 'source' | 'field' | 'action'>>;

// Action variables match on action, others on source. `field` alone is not
// unique (all simple attributes use `value`), and an action variable carrying
// no action id must resolve to nothing rather than to another action's
// identically-keyed field.
export const findVariableField = (
  variable: VariableFieldRef,
  fields: FieldMappingFieldItem[]
): FieldMappingFieldItem | undefined => {
  const actionId = variable.action && variable.action !== 'undefined' ? variable.action : undefined;
  const isActionVariable = variable.source === 'action' || actionId !== undefined;
  return fields.find((field) => {
    if (field.field !== variable.field) {
      return false;
    }
    // `field.action` is undefined on every non-action field, so an action
    // variable with no usable id must fail rather than compare undefined.
    return isActionVariable
      ? actionId !== undefined && field.action === actionId
      : field.source === variable.source;
  });
};

// Traveller and attribute sources identify their group by its fields rather
// than by a group id, so that arm delegates to findVariableField.
export const findVariableGroup = (
  variable: VariableFieldRef,
  variableGroups: FieldMappingGroup[]
): FieldMappingGroup | undefined => {
  return variableGroups.find((group) => {
    if (variable.action && group.id === variable.action) {
      return true;
    }
    if (isTravellerVariableSource(variable.source)) {
      return Boolean(findVariableField(variable, group.fields));
    }
    if (variable.source === 'company' && group.id === 'company') {
      return true;
    }
    if (variable.source === 'journey_data' && group.id === 'journeyData') {
      return true;
    }
    return group.id === variable.source;
  });
};

export const getVariableLabel = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
) => {
  // Gate on the broken marker, not a failed group lookup: the previews call this
  // without variableGroups (every lookup fails there, but healthy vars still need
  // a prefix). A broken var's label is already a qualified "Source • Field", so
  // return as-is -- prepending "Action" would give "Action • Email • Subject".
  if (isVariableSourceMissing(variable)) {
    return variable.label;
  }

  const prettySource = getVariableSource(variable, variableGroups);
  const variableLabel =
    variableGroups
      ?.map((group) => findVariableField(variable, group.fields)?.label)
      .find((label) => label !== undefined) ?? variable.label;

  const prefix = `${prettySource}${labelSeparator}`;
  if (variableLabel.startsWith(prefix)) {
    return variableLabel;
  }
  return `${prefix}${variableLabel}`;
};
